import type { useGT } from 'gt-react';
import {
  EyeIcon,
  FloppyDiskIcon,
  FunnelIcon,
  ListChecksIcon,
  ScalesIcon,
  StackIcon,
  TableIcon,
  TextAaIcon,
  UserIcon,
} from '@phosphor-icons/react';
import type {
  WizardStepDef,
  WizardValidationResult,
} from '@erp/ui/hooks/use-wizard';
import { CorrectionStrategy } from '@erp/shared-types';
import {
  consistentFieldsProblem,
  groupingStepDecision,
  previewStepDecision,
  saveStepProblems,
  schemaStepDecision,
  strategyStepDecision,
  triggerStepProblem,
} from './validationRuleWizardChecks';
import type { ValidationRuleWizardState } from './useValidationRuleWizardState';

/**
 * The rule builder's nine steps, built from the current state. Each `validate`
 * reads a decision from the tested pure checks and owns only the side effect
 * that goes with it (running the preview, recording a field error) — so the
 * checks that mirror the server's own rules are testable without a wizard.
 */
export function buildValidationRuleWizardSteps(
  state: ValidationRuleWizardState,
  gt: ReturnType<typeof useGT>,
): WizardStepDef[] {
  return [
    {
      id: 'schema',
      title: gt('Table'),
      description: gt('The table whose rows this rule compares.'),
      icon: TableIcon,
      validate: (): WizardValidationResult => {
        const decision = schemaStepDecision({
          schemaName: state.schemaName,
          knownSchemaNames: state.schemas.map((schema) => schema.name),
        });
        if (decision === 'ok') return true;
        return {
          ok: false,
          fields: {
            schemaName:
              decision === 'need-schema'
                ? gt('Pick the table to validate.')
                : gt('That table is no longer one you can validate.'),
          },
        };
      },
    },
    {
      id: 'filters',
      title: gt('Row filters'),
      description: gt(
        'Optional. Narrow the rows the rule reads; skip to read them all.',
      ),
      icon: FunnelIcon,
      optional: true,
    },
    {
      id: 'grouping',
      title: gt('Grouping'),
      description: gt('The fields that decide which rows belong together.'),
      icon: StackIcon,
      validate: (): WizardValidationResult => {
        if (
          groupingStepDecision({ groupingFields: state.groupingFields }) ===
          'ok'
        ) {
          return true;
        }
        return {
          ok: false,
          fields: {
            groupingFields: gt('Pick at least one field to group rows by.'),
          },
        };
      },
    },
    {
      id: 'consistent',
      title: gt('Must agree'),
      description: gt('The fields the rule requires to match within a group.'),
      icon: ListChecksIcon,
      validate: (): WizardValidationResult => {
        const problem = consistentFieldsProblem({
          groupingFields: state.groupingFields,
          validatedFields: state.validatedFields,
        });
        if (!problem) return true;
        return {
          ok: false,
          fields: {
            validatedFields:
              problem.kind === 'need-validated'
                ? gt('Pick at least one field that has to agree.')
                : gt(
                    '{fields} cannot both group the rows and be checked for agreement.',
                    { fields: problem.fields.join(', ') },
                  ),
          },
        };
      },
    },
    {
      id: 'normalization',
      title: gt('Comparison'),
      description: gt(
        'How two cells are compared before they count as different.',
      ),
      icon: TextAaIcon,
    },
    {
      id: 'strategy',
      title: gt('Proposed fix'),
      description: gt('What the rule proposes for a group that disagrees.'),
      icon: ScalesIcon,
      validate: (): WizardValidationResult => {
        const decision = strategyStepDecision({
          correctionStrategy: state.correctionStrategy,
          preferredDataSourceId: state.preferredDataSourceId,
          divisionIds: state.divisions.map((division) => division.id),
        });
        if (decision === 'ok') return true;
        return {
          ok: false,
          fields: {
            preferredDataSourceId:
              decision === 'need-preferred-division'
                ? gt('Pick the division whose value wins.')
                : gt('That division has no copy of this table.'),
          },
        };
      },
    },
    {
      id: 'assignment',
      title: gt('Owner'),
      description: gt('Who owns what the rule flags, and when it runs.'),
      icon: UserIcon,
      validate: (): WizardValidationResult => {
        const problem = triggerStepProblem({
          executionTrigger: state.executionTrigger,
          scheduleCron: state.scheduleCron,
        });
        if (!problem) return true;
        return {
          ok: false,
          fields: {
            scheduleCron:
              problem === 'need-cron'
                ? gt('Enter the schedule this rule should run on.')
                : gt('That is not a cron expression this can read.'),
          },
        };
      },
    },
    {
      id: 'preview',
      title: gt('Preview'),
      description: gt(
        'A capped, read-only evaluation of the rule as it stands. Nothing is saved.',
      ),
      icon: EyeIcon,
      validate: async (): Promise<WizardValidationResult> => {
        const decision = previewStepDecision({
          definitionKey: state.definitionKey,
          previewedKey: state.previewedKey,
          previewFailed: state.previewError !== null,
          previewing: state.previewing,
        });
        if (decision === 'ok') return true;
        if (decision === 'wait') {
          return {
            ok: false,
            message: gt('Still evaluating the rule — one moment.'),
          };
        }
        if (decision === 'blocked') {
          return {
            ok: false,
            message:
              state.previewError ??
              gt('Fix the rule before continuing — the preview could not run.'),
          };
        }
        const ran = await state.runPreview();
        return ran.ok
          ? true
          : {
              ok: false,
              // The server's own rejection, shown as it came — read off the
              // run's result, because `state` here is the snapshot from before
              // the await and its `previewError` is still the previous value.
              message:
                ran.message ??
                gt('The preview could not run against this rule.'),
            };
      },
    },
    {
      id: 'save',
      title: gt('Save'),
      description: gt('Name the rule, then save it as a draft or activate it.'),
      icon: FloppyDiskIcon,
      gate: true,
      confirm: () => ({
        title: state.activateOnSave
          ? gt('Save and activate this rule?')
          : gt('Save this rule as a draft?'),
        detail: state.activateOnSave
          ? gt(
              'An active rule can be run, and what it flags counts towards the open violations on the rules list.',
            )
          : gt('A draft is saved but never runs on its own.'),
        consequences: buildSaveConsequences(state, gt),
        confirmLabel:
          state.mode === 'edit' ? gt('Save changes') : gt('Save rule'),
      }),
      validate: (): WizardValidationResult => {
        const problems = saveStepProblems({ name: state.name });
        if (!problems.name) return true;
        return {
          ok: false,
          fields: {
            name:
              problems.name === 'missing-name'
                ? gt('Name the rule.')
                : gt('That name is too long.'),
          },
        };
      },
    },
  ];
}

/** The plain-language lines the gate confirmation lists before saving. */
function buildSaveConsequences(
  state: ValidationRuleWizardState,
  gt: ReturnType<typeof useGT>,
): string[] {
  const lines = [
    gt('Compares {fields} across {divisions} divisions of {table}', {
      fields: state.validatedFields.join(', ') || '—',
      divisions: String(state.selectedSchema?.divisionCount ?? 0),
      table: state.schemaName,
    }),
    gt('Grouped by {fields}', { fields: state.groupingFields.join(', ') }),
  ];

  if (state.rowFilters.length > 0) {
    lines.push(
      gt('Reads only the rows matching {count} filters', {
        count: String(state.rowFilters.length),
      }),
    );
  }

  lines.push(
    state.correctionStrategy === CorrectionStrategy.None
      ? gt('Proposes nothing — a human decides each disagreement')
      : gt('Proposes a value for each disagreement it can resolve'),
  );

  if (state.preview) {
    lines.push(
      gt('The last preview flagged {count} groups', {
        count: String(state.preview.stats.violatingGroups),
      }),
    );
  }

  return lines;
}
